import { onScopeDispose, ref, watch, type Ref } from 'vue';
import type { NxTableFocusScope } from '../focusScope';

/**
 * Why the table asks "is the user still editing here?" — every source of that
 * question, each deferred until its own gesture has settled:
 *
 * | cause                | source                                   | deferred by        |
 * |----------------------|------------------------------------------|--------------------|
 * | `focusout`           | focus left the wrapper                   | microtask + frame  |
 * | `release`            | `releaseTableFocus()` (consumer)         | microtask + frame  |
 * | `pointer-settled`    | a pointer gesture that began in the grid | two frames after up|
 * | `transition-settled` | a lazy cursor hand-off finished          | microtask + frame  |
 * | `outside-pointer`    | pointer down outside table and its chrome| a task (timeout 0) |
 *
 * The deferrals differ on purpose: blur handlers must commit, a popover opened
 * in the same tick must receive focus, and a one-click cell switch briefly
 * leaves `<body>` focused while Vue swaps the lazy editors. The ANSWER is given
 * in one place, `decideEditExit` (I-2172) — it used to be spread over three
 * handlers with slightly different rules and a late-bound callback.
 */
export type EditExitCause =
  | 'focusout'
  | 'release'
  | 'pointer-settled'
  | 'transition-settled'
  | 'outside-pointer';

export function useTableFocusOwnership(params: {
  wrapperRef: Readonly<Ref<HTMLElement | undefined>>;
  /** Floating content claimed for this table (see `focusScope.ts`). */
  focusScope: NxTableFocusScope;
  /** The keyboard/edit cursor. Leaving edit mode means clearing it. */
  cursor: Readonly<Ref<unknown>>;
  clearCursor: () => void;
  /** A cursor move is mounting its lazy editor; focus may sit on <body> meanwhile. */
  transitionPending: Readonly<Ref<boolean>>;
  /** Keyboard entry into the wrapper (Tab): focus the remembered cell. */
  onKeyboardEntry: () => void;
  /** Focus released on purpose (e.g. drop the wheel latch). */
  onRelease?: () => void;
}) {
  const { wrapperRef, focusScope } = params;

  /**
   * Whether the table owns focus. Flips to false when the user clicks/tabs out —
   * the selection then renders "passive", so it is clear the table no longer
   * drives input.
   */
  const tableFocused = ref(false);

  /** Programmatic wrapper focus must not look like keyboard Tab entry. */
  let suppressEntry = false;
  /** A pointer gesture that started inside the grid is still running. */
  let pointerActive = false;
  /** Serial of pointer gestures; a stale settle of an older gesture is ignored. */
  let pointerGesture = 0;

  /**
   * True when `el` is still part of table editing: inside the wrapper, inside
   * floating content claimed for this table (the kit's popovers, dropdowns,
   * modals and panels opened from it, including the table's own dialogs), or
   * inside a modal dialog: a modal makes the rest of the page inert, so it was
   * opened from the current interaction (a line's detail dialog that the page
   * renders). An unrelated menu or popover elsewhere does not count (I-2172).
   */
  function focusBelongsToTableEdit(el: Element | null): boolean {
    if (!el || el === document.body || el === document.documentElement) return false;
    if (wrapperRef.value?.contains(el)) return true;
    if (focusScope.owns(el)) return true;
    return isInsideModalDialog(el);
  }

  /** The one answer to "leave edit mode?" — see the table above. */
  function decideEditExit(cause: EditExitCause): void {
    const active = document.activeElement instanceof Element ? document.activeElement : null;
    if (cause === 'outside-pointer') {
      // A genuine pointer outside is the user's choice: it overrides a pending
      // hand-off and a stale "focused" flag, unless focus really is in the grid.
      if (!params.cursor.value) return;
      if (tableFocused.value && active && wrapperRef.value?.contains(active)) return;
      if (focusBelongsToTableEdit(active)) return;
      params.clearCursor();
      tableFocused.value = false;
      return;
    }
    // The table still owns focus, or a gesture / hand-off is still running:
    // its own settle asks again.
    if (tableFocused.value || pointerActive || params.transitionPending.value) return;
    if (focusBelongsToTableEdit(active)) {
      // Still editing through floating UI — keep the cursor and active chrome.
      tableFocused.value = true;
      return;
    }
    if (params.cursor.value) params.clearCursor();
  }

  /** Ask after blur commits settled and a same-tick popover received focus. */
  function askAfterFocusSettles(cause: EditExitCause): void {
    queueMicrotask(() => requestAnimationFrame(() => decideEditExit(cause)));
  }

  function onWrapperFocusInOut(event: FocusEvent): void {
    if (event.type === 'focusin') {
      tableFocused.value = true;
      return;
    }
    const next = event.relatedTarget as Node | null;
    if (next && wrapperRef.value?.contains(next)) return;
    tableFocused.value = false;
    askAfterFocusSettles('focusout');
  }

  /**
   * Any real interaction with the grid (wheel, scrollbar, click) puts keyboard
   * focus here — no second click into a cell needed. `preventScroll` keeps a
   * claim on wheel from jumping the page; `{ focus: false }` (pointerdown) only
   * marks ownership, so a scrollbar-thumb drag is not cancelled.
   */
  function claimTableFocus(opts?: { focus?: boolean }): void {
    tableFocused.value = true;
    if (opts?.focus === false) return;
    const wrap = wrapperRef.value;
    if (!wrap) return;
    const active = document.activeElement;
    if (active instanceof Node && wrap.contains(active)) return;
    // Otherwise wrapper focus would open the remembered editor first and the
    // actual pointer target another one right after.
    suppressEntry = true;
    try {
      wrap.focus({ preventScroll: true });
    } finally {
      suppressEntry = false;
    }
  }

  /**
   * The wrapper is one steady tab stop (I-2833): Tab or Shift+Tab from the page
   * enters the table through it and focuses the remembered cell. Focus coming
   * back from inside the table — a Shift+Tab from its first header control —
   * stops on the wrapper, so the next Shift+Tab leaves for the page the way the
   * walk came in.
   *
   * A press on a cell that cannot take the focus itself (an inert editor of a
   * passive row) focuses the wrapper too — that is no entry: the press's click
   * focuses the clicked cell. Entering first jumped to the remembered or first
   * field and scrolled it into view, so the clicked cell moved away and the
   * click missed it (BLP/nx#2818).
   */
  function onWrapperFocus(event?: FocusEvent): void {
    if (suppressEntry || pointerActive) return;
    const from = event?.relatedTarget;
    if (from instanceof Node && wrapperRef.value?.contains(from)) return;
    params.onKeyboardEntry();
  }

  /**
   * Parks the keyboard focus on the table itself — out of the cell it was in,
   * without entering the remembered cell again (Escape out of an editor). The
   * table stays the focus owner, so its cursor and edit state stay put.
   */
  function parkTableFocus(): void {
    const wrap = wrapperRef.value;
    if (!wrap) return;
    tableFocused.value = true;
    suppressEntry = true;
    try {
      wrap.focus({ preventScroll: true });
    } finally {
      suppressEntry = false;
    }
  }

  function releaseTableFocus(): void {
    pointerActive = false;
    pointerGesture++;
    tableFocused.value = false;
    params.onRelease?.();
    const wrap = wrapperRef.value;
    const active = document.activeElement;
    if (wrap && active instanceof HTMLElement && wrap.contains(active)) active.blur();
    askAfterFocusSettles('release');
  }

  // Pointer-down on a passive lazy cell blurs the mounted editor before the
  // target cell's click handler can mount its replacement. The gesture keeps
  // that hand-off inside the grid; otherwise the focusout answer would see
  // <body>, clear the new cursor and turn a one-click switch into a no-op.
  function onWindowPointerUp(): void {
    window.removeEventListener('pointerup', onWindowPointerUp);
    window.removeEventListener('pointercancel', onWindowPointerUp);
    claimTableFocus();
    const gesture = pointerGesture;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (gesture !== pointerGesture) return;
        pointerActive = false;
        decideEditExit('pointer-settled');
      });
    });
  }

  function onWrapperPointerDown(): void {
    pointerActive = true;
    pointerGesture++;
    claimTableFocus({ focus: false });
    window.addEventListener('pointerup', onWindowPointerUp, { once: true });
    window.addEventListener('pointercancel', onWindowPointerUp, { once: true });
  }

  // A lazy cursor move leaves <body> focused while Vue swaps the editors. Ask
  // once that controlled hand-off settled; a genuine outside pointer clears
  // through `outside-pointer` meanwhile.
  watch(params.transitionPending, (pending, wasPending) => {
    if (!pending && wasPending && !tableFocused.value) askAfterFocusSettles('transition-settled');
  });

  /** Click outside (not just focusout) — covers popover-return-to-body and a passive cursor. */
  function onDocumentPointerDown(event: PointerEvent): void {
    if (!params.cursor.value) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (focusBelongsToTableEdit(target)) return;
    // After this gesture's blur/focus settled.
    window.setTimeout(() => decideEditExit('outside-pointer'), 0);
  }

  watch(
    () => !!params.cursor.value,
    (hasCursor) => {
      if (hasCursor) document.addEventListener('pointerdown', onDocumentPointerDown, true);
      else document.removeEventListener('pointerdown', onDocumentPointerDown, true);
    },
    { flush: 'sync', immediate: true }
  );

  onScopeDispose(() => {
    document.removeEventListener('pointerdown', onDocumentPointerDown, true);
    window.removeEventListener('pointerup', onWindowPointerUp);
    window.removeEventListener('pointercancel', onWindowPointerUp);
  });

  return {
    tableFocused,
    claimTableFocus,
    parkTableFocus,
    releaseTableFocus,
    onWrapperFocus,
    onWrapperFocusInOut,
    onWrapperPointerDown,
    focusBelongsToTableEdit,
  };
}

function isInsideModalDialog(el: Element): boolean {
  if (el.closest('[aria-modal="true"]')) return true;
  const dialog = el.closest('dialog[open]');
  if (!dialog) return false;
  try {
    return dialog.matches(':modal');
  } catch {
    // No `:modal` support: an open <dialog> is the kit's modal.
    return true;
  }
}
